import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldAlert, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { API } from "../../App";

const money = (v) =>
  v == null ? "—" : `€${Number(v).toLocaleString("en-IE", {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PERIODS = [
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "180", label: "Last 6 months" },
  { value: "365", label: "Last 12 months" },
];

/**
 * Installer scorecard — ADMIN ONLY. Staff never see this component and
 * `GET /redos/scorecard` refuses a staff token, so there is nothing to
 * leak even if it were rendered by accident.
 *
 * Grouped by the ORIGINAL job's completion date, so a rate always reads
 * "of the jobs done in this period, how many came back". Only
 * installer-fault reasons count; film defect and customer-caused are
 * shown but never counted against anyone.
 */
export const InstallerScorecard = () => {
  const [days, setDays] = useState("90");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/redos/scorecard`, { params: { days } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the scorecard");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="installer-scorecard">
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="text-white flex items-center gap-2 text-lg">
          <ShieldAlert className="w-5 h-5 text-saturn-orange" />
          Installer scorecard
        </CardTitle>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-44 bg-slate-900 border-slate-700 text-white"
                         data-testid="scorecard-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}

        {data && !loading && (data.installers || []).length === 0 && (
          <p className="text-sm text-slate-400" data-testid="scorecard-empty">
            No jobs completed in this period.
          </p>
        )}

        {data && !loading && (data.installers || []).map((inst) => (
          <div key={inst.installer_id}
               className="bg-slate-900/50 border border-slate-700 rounded-lg p-4 space-y-3"
               data-testid={`scorecard-row-${inst.installer_id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-white font-semibold">{inst.installer_name}</span>
                {inst.small_sample && (
                  <Badge className="bg-slate-700/60 text-slate-300 border-slate-600 text-xs"
                         data-testid={`scorecard-small-sample-${inst.installer_id}`}>
                    small sample — under {data.small_sample_threshold} jobs
                  </Badge>
                )}
              </div>
              <span className={`text-2xl font-bold ${
                (inst.redo_rate_pct || 0) >= 10 ? "text-red-400"
                  : (inst.redo_rate_pct || 0) > 0 ? "text-amber-400" : "text-green-400"}`}
                    data-testid={`scorecard-rate-${inst.installer_id}`}>
                {inst.redo_rate_pct == null ? "—" : `${inst.redo_rate_pct}%`}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
              {[
                ["Jobs completed", inst.jobs_completed],
                ["Redos attributed", inst.redos_attributed],
                ["Redos on their jobs", inst.redos_total],
                ["Redo cost", money(inst.redo_cost)],
                ["Hours spent", Number(inst.hours_spent || 0).toFixed(2)],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs text-slate-400">{label}</p>
                  <p className="text-white font-medium">{value}</p>
                </div>
              ))}
            </div>

            {inst.redos_attributed > 0 && (
              <p className="text-xs text-slate-400"
                 data-testid={`scorecard-labour-${inst.installer_id}`}>
                Redo labour {money(inst.redo_labour_cost)} ·{" "}
                {Number(inst.hours_spent || 0).toFixed(2)}h on return visits
                {(inst.redo_labour_reasons || []).length > 0
                  ? ` — ${inst.redo_labour_reasons.join("; ")}`
                  : ""}
                {" · materials "}{money(inst.redo_materials_cost)}
              </p>
            )}

            {(inst.by_reason || []).length > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-slate-400">Why they came back</p>
                {inst.by_reason.map((r) => (
                  <div key={r.reason}
                       className="flex items-center justify-between text-xs bg-slate-800/50 rounded px-3 py-1.5">
                    <span className="text-slate-300">
                      {r.label} · {r.count}
                      {!r.counted_against_installer && (
                        <span className="text-slate-500"> · not counted against them</span>
                      )}
                    </span>
                    <span className="text-slate-400">
                      {money(r.cost)} · {Number(r.hours || 0).toFixed(2)}h
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {data && (data.notes || []).length > 0 && (
          <div className="text-xs text-slate-500 space-y-1 pt-2 border-t border-slate-700">
            {data.notes.map((n) => (
              <p key={n} className="flex gap-2">
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />{n}
              </p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InstallerScorecard;
